// Dropdown — single-select custom (não usa <select>/<option> nativos), pra ter
// aparência idêntica em qualquer SO. Trigger + popover com role=listbox.
// A11y/teclado: setas ↑↓ movem a opção ativa, Enter/Espaço seleciona, Esc fecha
// e devolve foco ao trigger, click-fora fecha. Visual no padrão do design system.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils/cn';
import styles from './Dropdown.module.css';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type DropdownSize = 'sm' | 'md';

export interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  size?: DropdownSize;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

const sizeClass: Record<DropdownSize, string> = {
  sm: styles.sm,
  md: styles.md,
};

export function Dropdown({
  value,
  onChange,
  options,
  size = 'md',
  placeholder = 'Selecione…',
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: DropdownProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : null;

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setActiveIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Click-fora fecha sem devolver foco ao trigger.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  // Ao abrir, foca a lista e ativa o item selecionado (ou o primeiro habilitado).
  useEffect(() => {
    if (!open) return;
    const start = selectedIndex >= 0 ? selectedIndex : firstEnabled(options);
    setActiveIndex(start);
    listRef.current?.focus();
  }, [open, selectedIndex, options]);

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
  };

  const choose = (index: number) => {
    const opt = options[index];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    close(true);
  };

  const moveActive = (delta: number) => {
    setActiveIndex((current) => nextEnabled(options, current, delta));
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openMenu();
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(firstEnabled(options));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(lastEnabled(options));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (activeIndex >= 0) choose(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
    }
  };

  return (
    <div ref={rootRef} className={cn(styles.root, sizeClass[size], className)}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={cn(styles.value, selectedLabel === null && styles.placeholder)}>
          {selectedLabel ?? placeholder}
        </span>
        <span className={styles.chevron} aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          aria-label={ariaLabel}
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
          className={styles.menu}
          onKeyDown={onListKeyDown}
        >
          {options.map((opt, i) => (
            // Padrão aria-activedescendant: o teclado é tratado no listbox
            // (onListKeyDown); as opções não são focáveis e só respondem ao
            // clique do mouse — daí o disable do click-events-have-key-events.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={opt.value}
              id={`${listboxId}-opt-${i}`}
              role="option"
              aria-selected={opt.value === value}
              aria-disabled={opt.disabled || undefined}
              className={styles.option}
              data-active={i === activeIndex || undefined}
              data-disabled={opt.disabled || undefined}
              onClick={() => choose(i)}
              onPointerMove={() => !opt.disabled && setActiveIndex(i)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function firstEnabled(options: DropdownOption[]): number {
  const i = options.findIndex((o) => !o.disabled);
  return i;
}

function lastEnabled(options: DropdownOption[]): number {
  for (let i = options.length - 1; i >= 0; i--) {
    if (!options[i].disabled) return i;
  }
  return -1;
}

function nextEnabled(options: DropdownOption[], from: number, delta: number): number {
  const n = options.length;
  if (n === 0) return -1;
  let i = from;
  for (let step = 0; step < n; step++) {
    i = (i + delta + n) % n;
    if (!options[i].disabled) return i;
  }
  return from;
}
